import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "className", "fixedChildren", "hideDefaultTooltip", "innerClassName", "matchContentWidth", "maxWidth", "onTruncatedChange", "placement", "reverse", "tooltip", "useFlex", "open", "onOpenChange", "defaultOpen"];
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useRef, useState, memo } from 'react';
import SyntheticEvent from '../core/SyntheticEvent';
import HiddenMeasure from '../layout/utils/HiddenMeasure';
import UITooltip from '../tooltip/UITooltip';
import { overflowsX } from '../utils/Overflows';
import { hidden } from '../utils/propTypes/decorators';
import { cancelSchedulerCallback, requestSchedulerCallback } from '../utils/Timers';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
const MUTATION_OBSERVER_CONFIG = {
  childList: true,
  subtree: true,
  characterData: true
};

/** @internal */

/** @internal */

/**
 * @internal
 * Adds `propTypes` and `defaultProps` keys to a component. This allows you to
 * then use a type assertion (`as`) in the `propTypes` and `defaultProps`
 * blocks to avoid type errors.
 *
 * @example
 * ```ts
 * type DefaultProps = typeof defaultProps;
 * type MyComponentWithPropTypes = ComponentWithPropTypes<
 *   ComponentType<Props>,
 *   Props,
 *   DefaultProps
 * >;
 * const defaultProps = {
 *   open: true;
 * };
 * (MyComponent as MyComponentWithPropTypes).propTypes = {
 *   open: PropTypes.bool.isRequired,
 * };
 * (MyComponent as MyComponentWithPropTypes).defaultProps = defaultProps;
 *
 * ```
 */

/** @internal */

const UITruncateString = /*#__PURE__*/memo(props => {
  const {
      children,
      className,
      fixedChildren,
      hideDefaultTooltip,
      innerClassName,
      matchContentWidth,
      maxWidth,
      onTruncatedChange,
      placement,
      reverse,
      tooltip,
      useFlex,
      open,
      onOpenChange,
      defaultOpen
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [contentWidth, setContentWidth] = useState(undefined);
  const [isTruncated, setIsTruncated] = useState(null);
  const wrapperRef = useRef(null);
  const measureRef = useRef(null);
  const tailRef = useRef(null);

  // Workaround for a truly bizarre Safari bug (#5825); still replicable as of Safari 13.0.5
  const keyRef = useRef(0);
  const hasMaxWidth = maxWidth != null;
  const hasMaxWidthRef = useRef(hasMaxWidth);
  if (hasMaxWidthRef.current !== hasMaxWidth) {
    keyRef.current += 1;
    hasMaxWidthRef.current = hasMaxWidth;
  }
  const key = keyRef.current;
  const updateTruncatedState = () => {
    const wrapperEl = wrapperRef.current;
    const tailEl = tailRef.current;
    if (matchContentWidth) {
      const measureEl = measureRef.current;
      if (measureEl instanceof HTMLElement && measureEl !== null) {
        setContentWidth(measureEl.clientWidth + 1);
      }
    }
    const newIsTruncated = reverse ? overflowsX(tailEl, wrapperEl) : overflowsX(wrapperEl);
    if (newIsTruncated !== isTruncated) {
      setIsTruncated(newIsTruncated);
      if (onTruncatedChange) onTruncatedChange(SyntheticEvent(newIsTruncated));
    }
  };

  // Using a scheduler callback ensures that measurement is batched, preventing extra layout costs
  const delayTimeoutRef = useRef(null);
  const delayedUpdateTruncatedState = () => {
    if (delayTimeoutRef.current) {
      cancelSchedulerCallback(delayTimeoutRef.current);
    }
    delayTimeoutRef.current = requestSchedulerCallback(updateTruncatedState);
  };

  // Add mount/unmount behavior
  useEffect(() => {
    const resizeObserver = new ResizeObserver(delayedUpdateTruncatedState);
    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }
    return () => {
      if (delayTimeoutRef.current) {
        cancelSchedulerCallback(delayTimeoutRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let mutationObserver;
    if (wrapperRef.current) {
      mutationObserver = new MutationObserver(() => {
        delayedUpdateTruncatedState();
      });
      mutationObserver.observe(wrapperRef.current, MUTATION_OBSERVER_CONFIG);
    }
    return () => {
      if (delayTimeoutRef.current) {
        cancelSchedulerCallback(delayTimeoutRef.current);
      }
      if (mutationObserver) mutationObserver.disconnect();
    };
  }, [matchContentWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  // When maxWidth is set, we need to set maxWidth on both the outer container and the inner
  // wrapper. We also need to set width to "unset" (if we aren't using matchContentWidth) in
  // order to prevent the table column from collapsing to 0 due to the `width: 100%` rule.
  const computedMaxWidth = maxWidth && contentWidth ? Math.min(maxWidth, contentWidth) : maxWidth || contentWidth;
  const style = computedMaxWidth ? {
    maxWidth: computedMaxWidth,
    width: maxWidth ? 'unset' : undefined
  } : undefined;
  const tailClassName = 'private-truncated-string__reverse__tail' + (isTruncated ? " is-truncated" : "");
  const renderedContent = reverse ? /*#__PURE__*/_jsxs("span", {
    className: "private-truncated-string__reverse",
    children: [isTruncated && /*#__PURE__*/_jsx("span", {
      className: "private-truncated-string__reverse__ellipse",
      children: "\u2026"
    }), /*#__PURE__*/_jsx("span", {
      className: tailClassName,
      children: /*#__PURE__*/_jsx("span", {
        className: "private-truncated-string__reverse__content",
        ref: tailRef,
        children: children
      })
    })]
  }) : /*#__PURE__*/_jsx("span", {
    children: children
  });
  const computedTooltip = tooltip === true ? children : tooltip;
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(UITooltip, {
      open: open,
      defaultOpen: defaultOpen,
      onOpenChange: onOpenChange,
      disabled: isTruncated === false || hideDefaultTooltip,
      placement: placement,
      title: computedTooltip,
      children: /*#__PURE__*/_createElement("span", Object.assign({}, rest, {
        className: classNames('private-truncated-string', className, useFlex && 'private-truncated-string--is-flex', Boolean(isTruncated && computedTooltip && !hideDefaultTooltip) && 'private-truncated-string--has-tooltip'),
        "data-content": true,
        key: key,
        style: style
      }), /*#__PURE__*/_jsx("span", {
        className: classNames('private-truncated-string__inner', innerClassName),
        ref: wrapperRef,
        style: style,
        children: renderedContent
      }), fixedChildren)
    }), matchContentWidth && /*#__PURE__*/_jsx(HiddenMeasure, {
      ref: measureRef,
      children: children
    })]
  });
});
UITruncateString.propTypes = {
  children: PropTypes.node.isRequired,
  fixedChildren: PropTypes.node,
  innerClassName: PropTypes.string,
  hideDefaultTooltip: PropTypes.bool,
  matchContentWidth: PropTypes.bool.isRequired,
  maxWidth: PropTypes.number,
  onTruncatedChange: hidden(PropTypes.func),
  open: UITooltip.propTypes.open,
  defaultOpen: UITooltip.propTypes.defaultOpen,
  onOpenChange: UITooltip.propTypes.onOpenChange,
  placement: UITooltip.propTypes.placement,
  reverse: PropTypes.bool.isRequired,
  tooltip: PropTypes.oneOfType([PropTypes.bool, PropTypes.node, PropTypes.string]).isRequired,
  useFlex: PropTypes.bool.isRequired
};

/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
UITruncateString.defaultProps = {
  defaultOpen: false,
  hideDefaultTooltip: false,
  reverse: false,
  tooltip: true,
  matchContentWidth: false,
  useFlex: false
};
UITruncateString.displayName = 'UITruncateString';
export default UITruncateString;